import { useContext } from 'react'
import { GlobalContext } from '../GlobalContext'
import Button from '../formfields/Button'
import Card from '../ui-elements/Card'
import { getRequiredGroupsForChecklist, userMeetsRequiredGroups } from '../utils/getRequiredGroupsForChecklist'
import { getLoggedInUserGroupIds } from '../utils/getLoggedInUserGroupIds'

type Props = {
    processId: string
}

// shown only when a process has more than one instance running at once, so a user landing on the
// process (e.g. via its QR code) can pick which one they mean instead of one being guessed for them.
// instances whose current stage has a checklist the logged-in user can actually fill out are listed
// first; instances that don't apply to this user are listed underneath.
function ProcessInstanceChooser({
    processId
}: Props) {

    const {
        tableData,
        userData,
        processData,
        t
    } = useContext(GlobalContext)

    const processDefinition = processData && processData[processId]
    const processRecords: ObjectStringKeyAnyValue = (processDefinition && processDefinition.processRecords) || {}
    const recordIds = Object.keys(processRecords)
    const loggedInUserGroupIds = getLoggedInUserGroupIds(tableData, userData)

    const isEligibleForCurrentUser = (record: ObjectStringKeyAnyValue) => {
        const stageId = record.processRecord && record.processRecord.CurrentStageId
        const stage = processDefinition && processDefinition.Stages && processDefinition.Stages[stageId]
        const checklistIds: string[] = (stage && stage.Checklists) || []
        if (checklistIds.length === 0) {
            // ongoing stages with no checklists (e.g. next-steps only) apply to anyone
            return true
        }
        return checklistIds.some((checklistId) => {
            const checklist = tableData && tableData.Checklists && tableData.Checklists[checklistId]
            if (!checklist || checklist.isChecklistActive === false) return false
            const requiredGroupIds = getRequiredGroupsForChecklist(checklist, tableData && tableData.ChecklistCategories)
            return userMeetsRequiredGroups(loggedInUserGroupIds, requiredGroupIds)
        })
    }

    if (recordIds.length <= 1) {
        return null
    }

    const sortedRecordIds = [...recordIds].sort((a, b) => {
        const aEligible = isEligibleForCurrentUser(processRecords[a])
        const bEligible = isEligibleForCurrentUser(processRecords[b])
        if (aEligible === bEligible) return 0
        return aEligible ? -1 : 1
    })

    return <div className={`w-full flex flex-col gap-2`}>
        <h4 className={`text-lg font-righteous text-brandblue`}>{t('ProcessPages.ProcessInstanceChooser.heading')}</h4>
        <div className={`w-full flex flex-col gap-2`}>
            {sortedRecordIds.map((recordId) => {
                const record = processRecords[recordId]
                const stageId = record.processRecord && record.processRecord.CurrentStageId
                const stageName = processDefinition.Stages && processDefinition.Stages[stageId] && processDefinition.Stages[stageId].DisplayName
                const eligible = isEligibleForCurrentUser(record)

                return <Card key={recordId}>
                    <div className={`w-full flex flex-row justify-between items-center gap-2`}>
                        <div className={`flex flex-col gap-1 items-start`}>
                            {stageName && <p className={`text-sm`}>{t('ProcessPages.ProcessList.currentlyInStage', { stage: stageName })}</p>}
                            {!eligible && <p className={`text-xs text-gray-500`}>{t('ProcessPages.ProcessInstanceChooser.notApplicableToYou')}</p>}
                        </div>
                        <Button
                            text={t('Buttons.view')}
                            internalLinkUrl={`/process/${processId}/record/${recordId}`}
                            variant={eligible ? 'primary' : 'gray'}
                        />
                    </div>
                </Card>
            })}
        </div>
    </div>
}
export default ProcessInstanceChooser
